import { useEffect, useRef } from 'react';
import { useNodes, useNodesInitialized, useReactFlow, useUpdateNodeInternals } from '@xyflow/react';

/**
 * Custom hook to automatically fit the ReactFlow view when nodes are first loaded.
 *
 * Uses `useNodesInitialized` to wait until all nodes are measured, then calls
 * `updateNodeInternals` to force React Flow to recompute every node's handle
 * positions before fitting the view.  This second step is critical for flows
 * that contain block nodes with child nodes: child nodes have a `parentId` and
 * their absolute handle positions are only finalised after their parent has been
 * measured, which can be a tick later than `nodesInitialized` becoming `true`.
 * Without it, edge paths are calculated from stale handle positions and edges
 * appear invisible until the next viewport change forces a recalculation.
 */
export function useReactFlowAutoFit(options?: { padding?: number; duration?: number }) {
	const { fitView } = useReactFlow();
	const updateNodeInternals = useUpdateNodeInternals();
	const nodesInitialized = useNodesInitialized();
	const nodes = useNodes();
	// Stable ref so we can read the current node list inside the effect without
	// adding `nodes` to the dependency array (it changes reference every render).
	const nodesRef = useRef(nodes);
	nodesRef.current = nodes;

	const hasAutoFittedRef = useRef(false);
	const { padding = 0.2, duration = 800 } = options || {};

	useEffect(() => {
		if (nodesInitialized && !hasAutoFittedRef.current) {
			hasAutoFittedRef.current = true;
			// Flush handle-position recalculations for every node (including
			// children of block nodes), then fit once the browser has painted.
			updateNodeInternals(nodesRef.current.map((n) => n.id));
			window.requestAnimationFrame(() => {
				fitView({ padding, duration });
			});
		}
	}, [nodesInitialized, fitView, updateNodeInternals, padding, duration]);
}

